<template>
  <div
    v-if="isFeatureEnabled && featureFees?.length"
    class="floating-pane fading">
    <div class="floating-pane-header">
      <icon-fees />
      <span>{{ capitalize(i18n.t('messages.featureFees')) }}</span>
      <div class="actions">
        <div
          v-tooltip="capitalize(i18n.t('messages.featureFeesDescription'))"
          class="help">
          <icon-info />
        </div>
      </div>
    </div>
    <div class="floating-pane-content">
      <div
        v-if="isLoading"
        class="flex-h-centered m-3">
        <loader-small class="mr-2" />
        {{ capitalize(i18n.t('actions.loadingFeatureFees')) }}
      </div>
      <table
        v-else
        class="table table-striped compact feature-fee-list">
        <caption class="d-none">
          {{
            i18n.t('messages.featureFees')
          }}
        </caption>
        <thead class="feature-fee-list-header">
          <tr>
            <th>
              <span>{{ capitalize(i18n.t('names.feature')) }}</span>
            </th>
            <th>
              <span>{{ capitalize(i18n.t('names.fee')) }}</span>
            </th>
          </tr>
        </thead>
        <tbody class="feature-fee-list-body">
          <tr
            v-for="featureFee in featureFees"
            :key="featureFee.id"
            class="feature-fee-list-entry">
            <td>{{ capitalize(featureFee.name) }}</td>
            <td>{{ featureFee.valueFormatted }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import { IconFees, IconInfo } from '@/common/icons/monochrome';
  import FeatureFee from '@/common/models/FeatureFee';
  import FeatureFees from '@/common/models/FeatureFees';
  import { areArraysEqual, debounce } from '@/globals';
  import LocalCache from '@/utils/LocalCache';
  import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';

  const props = defineProps<{ featureKeys: string[] }>();
  const i18n = useI18n();
  const organizationId = LocalCache.get('organization').organization_id;
  const { getFeatureFees } = mapOrgAppActions();
  const featureFees = ref<FeatureFee[]>([]);
  const isLoading = ref(true);
  const { organizationSettings } = mapOrgAppState();

  const isFeatureEnabled = computed(() => organizationSettings.uiEnableFeeBreakdown);

  const timeout = null;

  const initialize = debounce(
    async () => {
      if (!isFeatureEnabled.value) return;
      const data = {
        organization_id: organizationId,
        feature_keys: props.featureKeys,
      };
      isLoading.value = true;
      const result: FeatureFees = await getFeatureFees({
        data,
      });
      featureFees.value = result.features;
      isLoading.value = false;
    },
    500,
    timeout,
  );

  watch(
    () => props.featureKeys,
    (val, old) => {
      if (!areArraysEqual(val, old)) initialize();
    },
  );
  onMounted(initialize);
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  table {
    font-size: 0.9em;
    margin-bottom: 0;

    thead {
      text-transform: uppercase;
      font-size: 0.8em;
    }
  }
</style>
